import { APIStatus } from "@/api/APIStatus.type";
import { PLAYSTYLES_PLUS_CAP } from "@/config/game";
import {
  trackAddedChainRecommendation,
  trackAddedEvolutionStep,
  trackFilter,
} from "@/events/evolutionsMixpanel";
import { DetailedPlayerType } from "@/pages/DetailedPlayer/DetailedPlayer";
import {
  RangeFilterType,
  SelectorFilterType,
  SelectorOption,
  UpdateFilterType,
} from "@/pages/Players/FiltersPanel/filterTypes";
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
// The builder's network calls and response parsing live in shared, so web
// and the app send the same requests and read them the same way.
import {
  type EvolutionChainRecommendationsResponse,
  type EvolutionStepsResponse,
  type ResponseEvolutionStep,
  type RevealChainsResponse,
  getEvolutionChainRecommendations,
  getEvolutionSteps,
  getPathwayOptions,
  revealChains as revealChainsApi,
} from "@repo/shared/api";
import {
  type EvolutionPickerSelection,
  applyUpgrades,
  chainFromResponse,
  chainsAwaitReveal,
  createPathwayDraft,
  deriveStepUpgrades,
  evolutionNameLookup,
  evolutionPathPair,
  groupUpgrades,
  selectDraftPathway,
  stepFromResponse,
  stepsFromResponse,
  withRevealedIds,
} from "@repo/shared/evolutions";
import { type TranslateFn } from "@repo/shared/i18n";
import { fetchBaseData } from "@repo/shared/redux";
import type {
  Cost,
  EvolutionChainRecommendation,
  EvolutionChainTagType,
  EvolutionStep,
  PositionUpgrade,
  PathwayDraft as SharedPathwayDraft,
  UpgradeType,
} from "@repo/shared/types";
import {
  type FilterValues,
  type StepFilters,
  inGameAttributesDict,
  toStepFilters,
} from "@repo/shared/utils";

import {
  MetaRatingsPlayer,
  PlayerRole,
  Tier,
  defaultPositions,
  getTier,
} from "../players/playersSlice";
import { bodyTypeIdToName } from "../squadBuilder/squadBuilderUtils";
import { AppDispatch, IRootState } from "../store";
import { convertToEvolutionBuilderPlayer } from "./convertToEvolutionBuilderPlayer";
import {
  Evolution,
  evolutionsSelector,
  fetchEvolutions,
} from "./evolutionsSlice";

export type EvolutionBuilderPlayer = Omit<
  MetaRatingsPlayer,
  "availableEvos" | "canEvolve" | "metaRatingZeroChem"
> & {
  height?: number;
  gender?: "MALE" | "FEMALE";
};

const CHAIN_TAG_KEYS = {
  "last-chance": "chainTags.lastChance",
  free: "chainTags.free",
  "low-effort": "chainTags.lowEffort",
  "best-silver": "chainTags.bestSilver",
  "best-bronze": "chainTags.bestBronze",
  "high-potential": "chainTags.highPotential",
  "5-star-5-star": "chainTags.fiveStarFiveStar",
  gullit: "chainTags.gullit",
  nainggolan: "chainTags.nainggolan",
} as const;

/** Display text for a chain tag; `t` is the evoBuilder namespace's. */
export const chainTagLabel = (
  tag: EvolutionChainTagType,
  t: TranslateFn<"evoBuilder">,
) => {
  const step = /^best-(\d+)-step$/.exec(tag);
  if (step) return t("chainTags.bestStep", { n: step[1] });
  const key = CHAIN_TAG_KEYS[tag as keyof typeof CHAIN_TAG_KEYS];
  return key ? t(key) : tag.toUpperCase().replaceAll("-", " ");
};

type EvolutionChainTagColors = {
  backgroundColor: string;
  borderColor?: string;
  textColor?: string;
};
export const EVOLUTION_CHAIN_TAG_COLORS: Record<
  EvolutionChainTagType,
  EvolutionChainTagColors
> = {
  // "Best X step" recommendations – slightly dimmed, still vibrant
  "best-1-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },
  "best-2-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },
  "best-3-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },
  "best-4-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },
  "best-5-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },
  "best-6-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },
  "best-7-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },
  "best-8-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },
  "best-9-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },
  "best-10-step": {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  },

  // Urgency tags
  "last-chance": {
    backgroundColor: "bg-evolution-tag-background-urgency",
    borderColor: "border-evolution-tag-border-urgency",
    textColor: "text-evolution-tag-text-urgency",
  },
  // Accessability tags:
  free: {
    backgroundColor: "bg-evolution-tag-background-accessability",
    borderColor: "border-evolution-tag-border-accessability",
    textColor: "text-evolution-tag-text-accessability",
  },
  "low-effort": {
    backgroundColor: "bg-evolution-tag-background-accessability",
    borderColor: "border-evolution-tag-border-accessability",
    textColor: "text-evolution-tag-text-accessability",
  },
  "best-silver": {
    backgroundColor: "bg-evolution-tag-background-best-silver",
    borderColor: "border-evolution-tag-border-best-silver",
    textColor: "text-evolution-tag-text-best-silver",
  },
  "best-bronze": {
    backgroundColor: "bg-evolution-tag-background-best-bronze",
    borderColor: "border-evolution-tag-border-best-bronze",
    textColor: "text-evolution-tag-text-best-bronze",
  },
  "high-potential": {
    backgroundColor: "bg-evolution-tag-background-high-potential",
    borderColor: "border-evolution-tag-border-high-potential",
    textColor: "text-evolution-tag-text-high-potential",
  },
  "5-star-5-star": {
    backgroundColor: "bg-evolution-tag-background-5-star-5-star",
    borderColor: "border-evolution-tag-border-5-star-5-star",
    textColor: "text-evolution-tag-text-5-star-5-star",
  },
  gullit: {
    backgroundColor: "bg-evolution-tag-background-gullit",
    borderColor: "border-evolution-tag-border-gullit",
    textColor: "text-evolution-tag-text-gullit",
  },
  nainggolan: {
    backgroundColor: "bg-evolution-tag-background-nainggolan",
    borderColor: "border-evolution-tag-border-nainggolan",
    textColor: "text-evolution-tag-text-nainggolan",
  },
};

export const getEvolutionChainTagColor = (
  tag: EvolutionChainTagType,
): EvolutionChainTagColors =>
  EVOLUTION_CHAIN_TAG_COLORS[tag] || {
    backgroundColor: "bg-evolution-tag-background-best-meta-length",
    borderColor: "border-evolution-tag-border-best-meta-length",
    textColor: "text-evolution-tag-text-best-meta-length",
  };

export type EvolutionChainItem = {
  evolutionStep: EvolutionStep;
  currentEvolutionPlayer: EvolutionBuilderPlayer;
};

export type EvolutionChain = EvolutionChainItem[];

export type CurrentStepData = {
  cost: Cost;
  requiredGames: number;
};

// RTK's requestId of the pathway-options fetch the draft is waiting on.
type PathwayDraft = SharedPathwayDraft & { requestId?: string };

type EvolutionsBuilderState = {
  stepStatus: APIStatus;
  chainRecommendationsStatus: APIStatus;
  basePlayer: EvolutionBuilderPlayer;
  totalUpgrades: UpgradeType[];
  currentEvolutionChain: number[];
  // Option choices per step of currentEvolutionChain, same length.
  currentPathwayChain: number[][];
  currentEvolutionPlayer: EvolutionBuilderPlayer | null;
  evolutionSteps: EvolutionStep[];
  initialEvolutionSteps: EvolutionStep[];
  evolutionChainRecommendations: EvolutionChainRecommendation[];
  currentStepData: CurrentStepData | null;
  // RTK's own per-dispatch requestId for the latest in-flight request. Store
  // scoped on purpose: the builder unmounts on every back-navigation, so a
  // component-local counter restarts and lets a stale response win.
  requestCounters: {
    evolutionSteps: string;
    chainRecommendations: string;
  };
  chainSetId?: string;
  pathwayDraft: PathwayDraft | null;
  // Include/Exclude filters - contains explicit list of included evolution IDs
  includedEvolutions: number[];
  mustIncludeEvolutions: number[];
  // Player filters for evolution builder
  playerFilters: (RangeFilterType | SelectorFilterType)[];
  excludedPlayStyles: number[];
  excludedPlayStylesPlus: number[];
  activeFilter: EvolutionPickerSelection;
};

// Initial player filters for evolution builder
const initialPlayerFilters: (RangeFilterType | SelectorFilterType)[] = [
  {
    label: "Gender",
    key: "gender",
    type: "SELECTOR_FILTER",
    useLabelAsId: true,
    options: [
      { id: 0, label: "Male", category: "Gender" },
      { id: 1, label: "Female", category: "Gender" },
    ],
    selectedOptions: [],
  },
  {
    label: "Rating",
    key: "rating",
    type: "RANGE_FILTER",
    range: [1, 99],
    minValue: null,
    maxValue: null,
  },
  {
    label: "Quality",
    key: "quality",
    type: "SELECTOR_FILTER",
    options: [
      { id: 0, label: "Gold", category: "Quality" },
      { id: 1, label: "Silver", category: "Quality" },
      { id: 2, label: "Bronze", category: "Quality" },
    ],
    selectedOptions: [],
    useLabelAsId: true,
  },
  {
    label: "Skill Moves",
    shortLabel: "SM",
    key: "skillMoves",
    type: "SELECTOR_FILTER",
    options: [
      { id: 1, label: "1", category: "Skill Moves" },
      { id: 2, label: "2", category: "Skill Moves" },
      { id: 3, label: "3", category: "Skill Moves" },
      { id: 4, label: "4", category: "Skill Moves" },
      { id: 5, label: "5", category: "Skill Moves" },
    ],
    selectedOptions: [],
  },
  {
    label: "Weak Foot",
    shortLabel: "WF",
    key: "weakFoot",
    type: "SELECTOR_FILTER",
    options: [
      { id: 1, label: "1", category: "Weak Foot" },
      { id: 2, label: "2", category: "Weak Foot" },
      { id: 3, label: "3", category: "Weak Foot" },
      { id: 4, label: "4", category: "Weak Foot" },
      { id: 5, label: "5", category: "Weak Foot" },
    ],
    selectedOptions: [],
  },
  {
    label: "Position",
    key: "position",
    type: "SELECTOR_FILTER",
    useLabelAsId: true,
    options: [...defaultPositions].map((position, i) => ({
      id: i,
      label: position.label,
      category: position.category,
    })),
    selectedOptions: [],
  },
  {
    label: "PlayStyles",
    key: "playStyles",
    type: "SELECTOR_FILTER",
    options: [],
    selectedOptions: [],
  },
  {
    label: "PlayStyles+",
    key: "playStylesPlus",
    type: "SELECTOR_FILTER",
    options: [],
    selectedOptions: [],
  },
  {
    label: "Body Type",
    key: "body-types",
    type: "SELECTOR_FILTER",
    options: Object.entries(bodyTypeIdToName)
      .slice(0, 11)
      .map(([id, name]) => ({
        id: parseInt(id),
        label: name[0],
        category: "Body Type",
      })),
    selectedOptions: [],
  } as SelectorFilterType,
  {
    label: "AcceleRATE",
    key: "accelerates",
    type: "SELECTOR_FILTER",
    useLabelAsId: true,
    options: [
      {
        id: 0,
        label: "Explosive",
        category: "AcceleRATE",
      },
      {
        id: 1,
        label: "Controlled",
        category: "AcceleRATE",
      },
      {
        id: 2,
        label: "Lengthy",
        category: "AcceleRATE",
      },
    ],
    selectedOptions: [],
  },
  // Face stats (main 6 attributes)
  {
    label: "Pace",
    key: "pace",
    type: "RANGE_FILTER",
    range: [1, 99],
    minValue: null,
    maxValue: null,
  },
  {
    label: "Shooting",
    key: "shooting",
    type: "RANGE_FILTER",
    range: [1, 99],
    minValue: null,
    maxValue: null,
  },
  {
    label: "Passing",
    key: "passing",
    type: "RANGE_FILTER",
    range: [1, 99],
    minValue: null,
    maxValue: null,
  },
  {
    label: "Dribbling",
    key: "dribblingTitle",
    type: "RANGE_FILTER",
    range: [1, 99],
    minValue: null,
    maxValue: null,
  },
  {
    label: "Defending",
    key: "defending",
    type: "RANGE_FILTER",
    range: [1, 99],
    minValue: null,
    maxValue: null,
  },
  {
    label: "Physicality",
    key: "physicality",
    type: "RANGE_FILTER",
    range: [1, 99],
    minValue: null,
    maxValue: null,
  },
  // In-game attributes
  ...Object.entries(inGameAttributesDict)
    .filter(
      ([key]) =>
        ![
          "pace",
          "shooting",
          "passing",
          "dribblingTitle",
          "defending",
          "physicality",
        ].includes(key),
    )
    .map(
      ([key, attribute]) =>
        ({
          label: attribute.label,
          key,
          type: "RANGE_FILTER",
          range: [1, 99],
          minValue: null,
          maxValue: null,
        }) as RangeFilterType,
    ),
  {
    label: "Max PlayStyles",
    key: "maxPlayStyles",
    type: "RANGE_FILTER",
    range: [0, 10],
    minValue: null,
    maxValue: null,
  } as RangeFilterType,
  {
    label: "Max PlayStyles+",
    key: "maxPlayStylesPlus",
    type: "RANGE_FILTER",
    range: [0, PLAYSTYLES_PLUS_CAP],
    minValue: null,
    maxValue: null,
  } as RangeFilterType,
  {
    label: "Max Chain Length",
    key: "max-chain-length",
    type: "RANGE_FILTER",
    range: [1, 19],
    minValue: null,
    maxValue: null,
  } as RangeFilterType,
  {
    label: "Max Number of Games",
    key: "max-games",
    type: "RANGE_FILTER",
    range: [1, 29],
    minValue: null,
    maxValue: null,
  } as RangeFilterType,
];

const initialState: EvolutionsBuilderState = {
  stepStatus: "idle",
  chainRecommendationsStatus: "idle",
  basePlayer: null,
  totalUpgrades: [],
  currentEvolutionChain: [],
  currentPathwayChain: [],
  currentEvolutionPlayer: null,
  evolutionSteps: [],
  initialEvolutionSteps: [],
  evolutionChainRecommendations: [],
  requestCounters: {
    evolutionSteps: "",
    chainRecommendations: "",
  },
  currentStepData: null,
  chainSetId: undefined,
  pathwayDraft: null,
  // Include/Exclude filters - contains explicit list of included evolution IDs
  includedEvolutions: [],
  mustIncludeEvolutions: [],
  // Player filters for evolution builder
  playerFilters: initialPlayerFilters,
  excludedPlayStyles: [],
  excludedPlayStylesPlus: [],
  activeFilter: { type: "state", state: "available" },
};

export const fetchEvolutionChainRecommendations = createAsyncThunk<
  {
    data: EvolutionChainRecommendationsResponse;
    evolutions: Evolution[];
    expiredEvolutions: Evolution[];
  },
  { playerRoleIds?: number[]; excludeCompleted?: boolean }
>(
  "evolutions/fetchEvolutionChainRecommendations",
  async ({ playerRoleIds }, { getState }) => {
    const state = getState() as IRootState;
    const {
      basePlayer,
      currentEvolutionPlayer,
      currentEvolutionChain,
      currentPathwayChain,
      includedEvolutions,
      mustIncludeEvolutions,
    } = state.evolutionsBuilderReducer;
    const { evolutions, expiredEvolutions } = evolutionsSelector(state);
    const player = currentEvolutionPlayer || basePlayer;
    const filters = evolutionBuilderFiltersAsApiParamsSelector(state);

    try {
      const data = await getEvolutionChainRecommendations({
        resourceId: player.resourceId,
        currentChain: currentEvolutionChain,
        currentPathwayChain,
        assetId: player.assetId,
        playerRoleIds: playerRoleIds,
        evoPathId: player.evoPathId,
        includedEvolutions,
        mustIncludeEvolutions,
        filters,
      });

      return { data, evolutions, expiredEvolutions };
    } catch (error) {
      console.error("Error fetching evolution chain recommendations", error);
      throw error;
    }
  },
);

export const fetchEvolutionSteps = createAsyncThunk<
  {
    data: EvolutionStepsResponse;
    initialEvoSteps?: ResponseEvolutionStep[];
  },
  { playerRoleIds?: number[] }
>("evolutions/fetchEvolutionSteps", async ({ playerRoleIds }, { getState }) => {
  const state = getState() as IRootState;
  const {
    basePlayer,
    currentEvolutionChain,
    currentPathwayChain,
    initialEvolutionSteps,
    includedEvolutions,
  } = state.evolutionsBuilderReducer;

  let response: {
    data: EvolutionStepsResponse;
    initialEvoSteps?: ResponseEvolutionStep[];
  };

  const filters = evolutionBuilderFiltersAsApiParamsSelector(state);
  const player =
    getCurrentEvolutionPlayer(state.evolutionsBuilderReducer) || basePlayer;

  // This indicates that the player has been "opened" with a chain of steps
  if (initialEvolutionSteps.length === 0 && currentEvolutionChain.length > 0) {
    const combinedResponse = await Promise.all([
      getEvolutionSteps({
        resourceId: player.resourceId,
        currentChain: currentEvolutionChain,
        currentPathwayChain,
        assetId: player.assetId,
        playerRoleIds: playerRoleIds,
        evoPathId: player.evoPathId,
        includedEvolutions,
        filters,
      }),
      getEvolutionSteps({
        resourceId: basePlayer.resourceId,
        currentChain: [],
        currentPathwayChain: [],
        assetId: basePlayer.assetId,
        playerRoleIds: playerRoleIds,
        evoPathId: basePlayer.evoPathId,
        includedEvolutions,
        filters,
      }),
    ]);
    response = {
      data: combinedResponse[0],
      initialEvoSteps: combinedResponse[1].nextSteps,
    };
  } else {
    const singleResponse = await getEvolutionSteps({
      resourceId: player.resourceId,
      currentChain: currentEvolutionChain,
      currentPathwayChain,
      assetId: player.assetId,
      playerRoleIds: playerRoleIds,
      evoPathId: player.evoPathId,
      includedEvolutions,
      filters,
    });
    response = {
      data: singleResponse,
      initialEvoSteps: undefined,
    };
  }
  return response;
});

export const fetchPathwayOptions = createAsyncThunk<
  EvolutionStepsResponse,
  { playerRoleIds?: number[] }
>("evolutions/fetchPathwayOptions", async ({ playerRoleIds }, { getState }) => {
  const state = (getState() as IRootState).evolutionsBuilderReducer;
  const { currentEvolutionChain, currentPathwayChain, pathwayDraft } = state;
  const player = getCurrentEvolutionPlayer(state);

  return getPathwayOptions({
    resourceId: player.resourceId,
    chain: [...currentEvolutionChain, pathwayDraft.gridStep.evolutionId],
    pathwayChain: [...currentPathwayChain, pathwayDraft.pathway],
    assetId: player.assetId,
    playerRoleIds,
    evoPathId: player.evoPathId,
  });
});

export const revealChains = createAsyncThunk<{
  data: RevealChainsResponse;
  evolutions: Evolution[];
  expiredEvolutions: Evolution[];
}>("evolutions/revealChains", async (_, { getState }) => {
  const state = getState() as IRootState;
  const { evolutionsBuilderReducer } = state;
  const { evolutions, expiredEvolutions } = evolutionsSelector(state);
  const player = getCurrentEvolutionPlayer(evolutionsBuilderReducer);
  try {
    const data = await revealChainsApi({
      resourceId: player.resourceId,
      chainSetId: evolutionsBuilderReducer.chainSetId,
    });
    return { data, evolutions, expiredEvolutions };
  } catch (error) {
    console.error("Error revealing chains", error);
    throw error;
  }
});

const evolutionsBuilderSlice = createSlice({
  name: "evolutions",
  initialState,
  reducers: {
    addPlayerToEvolutionBuilder: (
      state,
      action: PayloadAction<{
        player: MetaRatingsPlayer | DetailedPlayerType;
        evolutionChain?: number[];
        pathwayChain?: number[][];
      }>,
    ) => {
      const convertedPlayer = convertToEvolutionBuilderPlayer(
        action.payload.player,
      );
      if (action.payload.evolutionChain) {
        const { evolutionPath, pathwayPath } = evolutionPathPair(
          action.payload.evolutionChain,
          action.payload.pathwayChain ?? action.payload.player.pathwayPath,
        );
        state.currentEvolutionChain = evolutionPath;
        state.currentPathwayChain = pathwayPath;
      } else {
        state.currentEvolutionChain = [];
        state.currentPathwayChain = [];
        state.evolutionChainRecommendations = [];
        state.currentEvolutionPlayer = null;
        state.basePlayer = null;
        state.totalUpgrades = [];
        state.currentStepData = null;
        state.evolutionSteps = [];
        state.initialEvolutionSteps = [];
        state.stepStatus = "idle";
        state.chainRecommendationsStatus = "idle";
        state.requestCounters = {
          evolutionSteps: "",
          chainRecommendations: "",
        };
      }
      state.basePlayer = convertedPlayer;
      state.currentEvolutionPlayer = convertedPlayer;
    },
    addStepToEvolutionChain: (state, action: PayloadAction<EvolutionStep>) => {
      const step = state.evolutionSteps.find(
        (step) => step.evolutionId === action.payload.evolutionId,
      );
      if (!step || !step.evolutionId) {
        return;
      }
      trackAddedEvolutionStep();
      state.currentEvolutionChain.push(step.evolutionId);
      // The payload's pathway, so a pathway picked in the sheet wins over the
      // grid step's recommendation.
      state.currentPathwayChain.push(action.payload.pathwayPath ?? []);
    },
    openPathwayDraft: (state, action: PayloadAction<EvolutionStep>) => {
      state.pathwayDraft = createPathwayDraft(action.payload);
    },
    selectPathway: (state, action: PayloadAction<number[]>) => {
      if (!state.pathwayDraft) return;
      state.pathwayDraft = selectDraftPathway(
        state.pathwayDraft,
        action.payload,
      );
    },
    closePathwayDraft: (state) => {
      state.pathwayDraft = null;
    },
    addChainToEvolutionChain: (
      state,
      action: PayloadAction<{
        evolutionChain: number[];
        pathwayChain?: number[][];
      }>,
    ) => {
      const { evolutionPath, pathwayPath } = evolutionPathPair(
        action.payload.evolutionChain,
        action.payload.pathwayChain,
      );
      state.currentEvolutionChain = evolutionPath;
      state.currentPathwayChain = pathwayPath;
    },
    addChainRecommendationToChain: (
      state,
      action: PayloadAction<EvolutionChainRecommendation>,
    ) => {
      trackAddedChainRecommendation();
      const { evolutionPath, pathwayPath } = evolutionPathPair(
        action.payload.evolutionIds,
        action.payload.pathwayPath,
      );
      state.currentEvolutionChain.push(...evolutionPath);
      state.currentPathwayChain.push(...pathwayPath);
    },
    updateCurrentEvolutionPlayer: (
      state,
      action: PayloadAction<EvolutionBuilderPlayer>,
    ) => {
      state.currentEvolutionPlayer = action.payload;
    },

    removeStepFromChain: (state, action: PayloadAction<number>) => {
      state.currentEvolutionChain = state.currentEvolutionChain.slice(
        0,
        action.payload,
      );
      state.currentPathwayChain = state.currentPathwayChain.slice(
        0,
        action.payload,
      );
    },
    updatePlayerRoleChosenPlayer: (state, action: PayloadAction<number>) => {
      if (!action.payload) return;
      state.basePlayer.playerRoleId = action.payload;
      state.currentEvolutionPlayer.playerRoleId = action.payload;
    },
    clearEvolutionChain: (state) => {
      state.currentEvolutionChain = [];
      state.currentPathwayChain = [];
      state.evolutionSteps = [...state.initialEvolutionSteps];
      state.evolutionChainRecommendations = [];
      state.stepStatus = "idle";
      state.chainRecommendationsStatus = "idle";
      state.requestCounters = {
        evolutionSteps: "",
        chainRecommendations: "",
      };
    },
    removeEvolutionPlayer: (state) => {
      state.currentEvolutionChain = [];
      state.currentPathwayChain = [];
      state.evolutionChainRecommendations = [];
      state.currentEvolutionPlayer = null;
      state.basePlayer = null;
      state.currentStepData = null;
      state.totalUpgrades = [];
      state.evolutionSteps = [];
      state.initialEvolutionSteps = [];
      state.stepStatus = "idle";
      state.chainRecommendationsStatus = "idle";
      state.mustIncludeEvolutions = []; // TODO: Think about this.
      state.requestCounters = {
        evolutionSteps: "",
        chainRecommendations: "",
      };
    },
    setEvolutionIncluded: (state, action: PayloadAction<number>) => {
      const evolutionId = action.payload;
      // Remove from excluded list (making it included)
      const index = state.includedEvolutions.indexOf(evolutionId);
      if (index > -1) {
        state.includedEvolutions.splice(index, 1);
      }
    },
    setEvolutionExcluded: (state, action: PayloadAction<number>) => {
      const evolutionId = action.payload;
      // Add to excluded list if not already there
      if (!state.includedEvolutions.includes(evolutionId)) {
        state.includedEvolutions.push(evolutionId);
      }
    },
    // setMaxChainLength: (state, action: PayloadAction<number>) => {
    //   state.maxChainLength = action.payload;
    // },
    applyEvolutionFilters: (
      state,
      action: PayloadAction<{
        includedEvolutions: number[];
        mustIncludeEvolutions: number[];
      }>,
    ) => {
      // Only update includedEvolutions if the contents actually changed
      const newIncluded = action.payload.includedEvolutions;
      const currentIncluded = state.includedEvolutions;
      const includedEqual =
        newIncluded.length === currentIncluded.length &&
        newIncluded.every((id, index) => currentIncluded[index] === id);

      if (!includedEqual) {
        state.includedEvolutions = newIncluded;
      }

      const newMustInclude = action.payload.mustIncludeEvolutions;
      const currentMustInclude = state.mustIncludeEvolutions;
      const mustIncludeEqual =
        newMustInclude.length === currentMustInclude.length &&
        newMustInclude.every((id, index) => currentMustInclude[index] === id);

      if (!mustIncludeEqual) {
        state.mustIncludeEvolutions = newMustInclude;
      }

      // if (state.maxChainLength !== action.payload.maxChainLength) {
      //   state.maxChainLength = action.payload.maxChainLength;
      // }
    },
    clearEvolutionFilters: (state) => {
      state.includedEvolutions = [];
      state.mustIncludeEvolutions = [];
    },
    // Player filter actions
    updateEvolutionBuilderFilter: (
      state,
      action: PayloadAction<UpdateFilterType>,
    ) => {
      const { key } = action.payload;
      const filter = state.playerFilters.find((f) => f.key === key);

      if (filter?.type === "RANGE_FILTER") {
        const { min, max } = action.payload;
        if (min !== undefined) filter.minValue = min;
        if (max !== undefined) filter.maxValue = max;
      } else if (filter?.type === "SELECTOR_FILTER") {
        const { value } = action.payload;
        const isSelected = filter.selectedOptions.includes(value);

        if (isSelected) {
          filter.selectedOptions = filter.selectedOptions.filter(
            (so) => so !== value,
          );
        } else {
          filter.selectedOptions.push(value);
        }
      }
    },
    clearEvolutionBuilderFilter: (state, action: PayloadAction<string>) => {
      const filter = state.playerFilters.find((f) => f.key === action.payload);

      if (filter?.type === "RANGE_FILTER") {
        filter.minValue = null;
        filter.maxValue = null;
      } else if (filter?.type === "SELECTOR_FILTER") {
        filter.selectedOptions = [];
      }
    },
    clearEvolutionBuilderFilterOption: (
      state,
      action: PayloadAction<{ filterKey: string; option: number }>,
    ) => {
      const { filterKey, option } = action.payload;
      const filter = state.playerFilters.find((f) => f.key === filterKey);

      if (filter?.type === "SELECTOR_FILTER") {
        filter.selectedOptions = filter.selectedOptions.filter(
          (so) => so !== option,
        );
      }
    },
    clearAllEvolutionBuilderFilters: (state) => {
      state.playerFilters.forEach((filter) => {
        if (filter.type === "RANGE_FILTER") {
          filter.minValue = null;
          filter.maxValue = null;
        } else if (filter.type === "SELECTOR_FILTER") {
          filter.selectedOptions = [];
        }
      });
      state.excludedPlayStyles = [];
      state.excludedPlayStylesPlus = [];
    },
    updateEvolutionBuilderFilterOptions: (
      state,
      action: PayloadAction<{ key: string; options: SelectorOption[] }>,
    ) => {
      const filter = state.playerFilters.find(
        (f) => f.key === action.payload.key,
      );
      if (filter?.type === "SELECTOR_FILTER") {
        filter.options = action.payload.options;
      }
    },
    setExcludedPlayStyles: (state, action: PayloadAction<number[]>) => {
      state.excludedPlayStyles = action.payload;
    },
    setExcludedPlayStylesPlus: (state, action: PayloadAction<number[]>) => {
      state.excludedPlayStylesPlus = action.payload;
    },
    // Apply all filters at once (used by EvolutionBuilderFilters component)
    applyEvolutionBuilderFilters: (
      state,
      action: PayloadAction<{
        playerFilters: (RangeFilterType | SelectorFilterType)[];
        excludedPlayStyles: number[];
        excludedPlayStylesPlus: number[];
      }>,
    ) => {
      const {
        playerFilters: newPlayerFilters,
        excludedPlayStyles,
        excludedPlayStylesPlus,
      } = action.payload;

      let hasChanges = false;

      for (const newFilter of newPlayerFilters) {
        const existingFilter = state.playerFilters.find(
          (f) => f.key === newFilter.key,
        );
        if (!existingFilter) continue;
        if (
          existingFilter.type === "RANGE_FILTER" &&
          newFilter.type === "RANGE_FILTER"
        ) {
          if (
            existingFilter.minValue !== newFilter.minValue ||
            existingFilter.maxValue !== newFilter.maxValue
          ) {
            hasChanges = true;
            break;
          }
        } else if (
          existingFilter.type === "SELECTOR_FILTER" &&
          newFilter.type === "SELECTOR_FILTER"
        ) {
          const existingOptions = existingFilter.selectedOptions;
          const newOptions = newFilter.selectedOptions;
          if (
            existingOptions.length !== newOptions.length ||
            !existingOptions.every((opt, idx) => opt === newOptions[idx])
          ) {
            hasChanges = true;
            break;
          }
        }
      }

      const arraysEqual = (a: number[], b: number[]) =>
        a.length === b.length && a.every((v, i) => v === b[i]);

      if (
        !arraysEqual(state.excludedPlayStyles, excludedPlayStyles) ||
        !arraysEqual(state.excludedPlayStylesPlus, excludedPlayStylesPlus)
      ) {
        hasChanges = true;
      }

      if (!hasChanges) return;

      // Update player filters
      newPlayerFilters.forEach((newFilter) => {
        const existingFilter = state.playerFilters.find(
          (f) => f.key === newFilter.key,
        );
        if (existingFilter) {
          if (
            existingFilter.type === "RANGE_FILTER" &&
            newFilter.type === "RANGE_FILTER"
          ) {
            existingFilter.minValue = newFilter.minValue;
            existingFilter.maxValue = newFilter.maxValue;
          } else if (
            existingFilter.type === "SELECTOR_FILTER" &&
            newFilter.type === "SELECTOR_FILTER"
          ) {
            existingFilter.selectedOptions = [...newFilter.selectedOptions];
          }
        }
      });

      state.excludedPlayStyles = excludedPlayStyles;
      state.excludedPlayStylesPlus = excludedPlayStylesPlus;
    },
    setEvolutionBuilderActiveFilter: (
      state,
      action: PayloadAction<EvolutionPickerSelection>,
    ) => {
      state.activeFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEvolutionSteps.fulfilled, (state, action) => {
      // Ignore stale requests
      if (action.meta.requestId !== state.requestCounters.evolutionSteps) {
        return;
      }

      const {
        nextSteps: responseEvolutionSteps,
        detailedPlayer,
        baseStep,
        currentStep: totalUpgrades,
      } = action.payload.data;

      const initialEvoStepsResponse = action.payload.initialEvoSteps;
      const shouldUpdateInitialEvolutionSteps =
        state.initialEvolutionSteps.length === 0;

      state.basePlayer = convertToEvolutionBuilderPlayer(baseStep);
      state.currentEvolutionPlayer =
        convertToEvolutionBuilderPlayer(detailedPlayer);

      const isGk = state.currentEvolutionPlayer?.preferredPosition === "GK";
      state.currentStepData = {
        cost: totalUpgrades.cost,
        requiredGames: totalUpgrades.requiredGames,
      };
      state.totalUpgrades = deriveStepUpgrades(totalUpgrades, isGk);

      const responseSteps = parseSteps(responseEvolutionSteps, isGk);
      const newEvolutionSteps: EvolutionStep[] = [];

      if (shouldUpdateInitialEvolutionSteps) {
        const initialSteps = initialEvoStepsResponse
          ? parseSteps(initialEvoStepsResponse, isGk)
          : responseSteps;
        state.initialEvolutionSteps = initialSteps.map((step) =>
          buildStep(state.basePlayer, step),
        );
      }

      // A chosen evolution can unlock others (a new position opens new
      // evolutions), so steps missing from the initial list join it.
      const known = new Set(
        state.initialEvolutionSteps.map((step) => step.evolutionId),
      );
      const unlocked = responseSteps.filter(
        (step) => !known.has(step.evolutionId),
      );
      if (unlocked.length > 0) {
        state.initialEvolutionSteps = [
          ...state.initialEvolutionSteps,
          ...unlocked.map((step) => buildStep(state.basePlayer, step)),
        ];
      }

      // Offered now: the initial steps the response still has, measured from
      // the current player.
      state.initialEvolutionSteps.forEach((initialStep) => {
        const offered = responseSteps.findLast(
          (step) => step.evolutionId === initialStep.evolutionId,
        );
        if (offered) {
          newEvolutionSteps.push(
            buildStep(state.currentEvolutionPlayer, offered),
          );
        }
      });

      state.evolutionSteps = newEvolutionSteps;
      state.stepStatus = "fulfilled";
    });
    builder.addCase(
      fetchEvolutionChainRecommendations.fulfilled,
      (state, action) => {
        // Ignore stale requests
        if (
          action.meta.requestId !== state.requestCounters.chainRecommendations
        ) {
          return;
        }

        const { nextSteps, detailedPlayer, chainSetId } = action.payload.data;

        state.chainSetId = chainSetId;
        state.currentEvolutionPlayer =
          convertToEvolutionBuilderPlayer(detailedPlayer);

        const isGk = state.currentEvolutionPlayer?.preferredPosition === "GK";
        const nameOf = evolutionNameLookup(
          action.payload.evolutions,
          action.payload.expiredEvolutions,
        );
        state.evolutionChainRecommendations = nextSteps.map((responseStep) => {
          const chain = chainFromResponse(responseStep, isGk, nameOf);
          const newPlayer = applyEvolutionStep(
            state.currentEvolutionPlayer,
            chain,
          );
          return { ...chain, metaRating: newPlayer.metaRating };
        });

        state.chainRecommendationsStatus = "fulfilled";
      },
    );
    builder.addCase(
      fetchEvolutionChainRecommendations.pending,
      (state, action) => {
        state.chainRecommendationsStatus = "pending";
        state.requestCounters.chainRecommendations = action.meta.requestId;
      },
    );
    builder.addCase(
      fetchEvolutionChainRecommendations.rejected,
      (state, action) => {
        if (
          action.meta.requestId !== state.requestCounters.chainRecommendations
        ) {
          return;
        }
        state.chainRecommendationsStatus = "rejected";
      },
    );
    builder
      .addCase(fetchPathwayOptions.pending, (state, action) => {
        if (!state.pathwayDraft) return;
        state.pathwayDraft.loading = true;
        state.pathwayDraft.requestId = action.meta.requestId;
      })
      .addCase(fetchPathwayOptions.rejected, (state, action) => {
        if (state.pathwayDraft?.requestId !== action.meta.requestId) return;
        state.pathwayDraft.loading = false;
      })
      .addCase(fetchPathwayOptions.fulfilled, (state, action) => {
        const draft = state.pathwayDraft;
        if (draft?.requestId !== action.meta.requestId) return;
        // Diffs are measured from the player before the pathway evo, which is
        // the builder's current player, so neighbors build like grid steps.
        const currentPlayer = getCurrentEvolutionPlayer(state);
        draft.neighbors = stepsFromResponse(
          action.payload,
          currentPlayer.preferredPosition === "GK",
        ).map((step) => buildStep(currentPlayer, step));
        draft.loading = false;
      });
    builder.addCase(fetchEvolutionSteps.pending, (state, action) => {
      state.stepStatus = "pending";
      state.requestCounters.evolutionSteps = action.meta.requestId;
    });
    builder
      .addCase(fetchEvolutionSteps.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestCounters.evolutionSteps) {
          return;
        }
        state.stepStatus = "rejected";
      })
      .addCase(revealChains.fulfilled, (state, action) => {
        const nameOf = evolutionNameLookup(
          action.payload.evolutions,
          action.payload.expiredEvolutions,
        );
        state.evolutionChainRecommendations = action.payload.data.flatMap(
          (revealed) =>
            state.evolutionChainRecommendations
              .filter((chain) => chain.chainId === revealed.chainId)
              .map((chain) =>
                withRevealedIds(
                  chain,
                  {
                    evolutionIds: revealed.evolutionIdPath,
                    pathwayPath: revealed.pathwayPath,
                  },
                  nameOf,
                ),
              ),
        );
        state.chainRecommendationsStatus = "fulfilled";
      })
      .addCase(revealChains.pending, (state, action) => {
        state.chainRecommendationsStatus = "pending";
      })
      .addCase(revealChains.rejected, (state) => {
        state.chainRecommendationsStatus = "rejected";
      })
      // Initialize includedEvolutions when evolutions are fetched
      .addCase(fetchEvolutions.fulfilled, (state, action) => {
        // Only set if includedEvolutions is empty (initial state)
        if (state.includedEvolutions.length === 0) {
          state.includedEvolutions = action.payload.evolutions.reduce(
            (acc, evo) => {
              if (evo.state === "available") acc.push(evo.id);
              return acc;
            },
            [] as number[],
          );
        }
      })
      // Update playStyles filter options when base data is loaded
      .addCase(fetchBaseData.fulfilled, (state, action) => {
        const { playStyles } = action.payload;
        const playStyleOptions = Object.entries(playStyles).map(
          ([id, playStyle]) => ({
            id: parseInt(id),
            label: playStyle.name,
            value: id,
            category: playStyle.category,
          }),
        );

        // Update playStyles filter options
        const playStyleFilter = state.playerFilters.find(
          (f) => f.key === "playStyles",
        ) as SelectorFilterType;
        if (playStyleFilter) {
          playStyleFilter.options = playStyleOptions;
        }

        // Update playStylesPlus filter options
        const playStylePlusFilter = state.playerFilters.find(
          (f) => f.key === "playStylesPlus",
        ) as SelectorFilterType;
        if (playStylePlusFilter) {
          playStylePlusFilter.options = playStyleOptions;
        }
      });
  },
});

export const {
  addPlayerToEvolutionBuilder,
  removeStepFromChain,
  updatePlayerRoleChosenPlayer,
  addStepToEvolutionChain,
  openPathwayDraft,
  selectPathway,
  closePathwayDraft,
  clearEvolutionChain,
  updateCurrentEvolutionPlayer,
  removeEvolutionPlayer,
  addChainRecommendationToChain,
  addChainToEvolutionChain,
  setEvolutionExcluded,
  applyEvolutionFilters,
  clearEvolutionFilters,
  updateEvolutionBuilderFilter,
  clearEvolutionBuilderFilter,
  clearEvolutionBuilderFilterOption,
  clearAllEvolutionBuilderFilters,
  updateEvolutionBuilderFilterOptions,
  applyEvolutionBuilderFilters,
  setExcludedPlayStyles,
  setExcludedPlayStylesPlus,
  setEvolutionBuilderActiveFilter,
} = evolutionsBuilderSlice.actions;

export const evolutionsBuilderSelector = (state: IRootState) =>
  state.evolutionsBuilderReducer;

export const evolutionFiltersSelector = (state: IRootState) => ({
  includedEvolutions: state.evolutionsBuilderReducer.includedEvolutions,
  mustIncludeEvolutions: state.evolutionsBuilderReducer.mustIncludeEvolutions,
});

export const evolutionBuilderPlayerFiltersSelector = (state: IRootState) =>
  state.evolutionsBuilderReducer.playerFilters;

export const evolutionBuilderActiveFiltersSelector = (state: IRootState) => {
  const { playerFilters } = state.evolutionsBuilderReducer;
  return playerFilters.filter((filter) => {
    if (filter.type === "RANGE_FILTER")
      return filter.minValue !== null || filter.maxValue !== null;
    else return filter.selectedOptions.length > 0;
  });
};

// Fires the "filter" Mixpanel event for the Evolutions Builder. Implemented as a
// thunk so the active filters are read from the store (`getState`) at dispatch
// time — this runs right after the filters are applied, so it always sees the
// up-to-date state instead of a stale render snapshot. `hasChosenPlayerRole`
// comes from component state (not redux), so it's passed in explicitly.
export const trackBuilderFiltersApplied =
  ({ hasChosenPlayerRole }: { hasChosenPlayerRole: boolean }) =>
  (_dispatch: AppDispatch, getState: () => IRootState) => {
    const activeFilters = evolutionBuilderActiveFiltersSelector(getState());
    const filters = activeFilters.map((f) => f.key);
    if (hasChosenPlayerRole) {
      filters.push("playerRole");
    }
    trackFilter({
      subsection: "Builder",
      filters,
    });
  };

export const evolutionBuilderPlayStyleFiltersSelector = (
  state: IRootState,
) => ({
  excludedPlayStyles: state.evolutionsBuilderReducer.excludedPlayStyles,
  excludedPlayStylesPlus: state.evolutionsBuilderReducer.excludedPlayStylesPlus,
});

// Selector to convert filters to the API format
/**
 * The builder's filter descriptors as the canonical `FilterValues`, so the
 * `/one-steps` body is written by shared's `toStepFilters` — the same
 * function the app uses — rather than a second time here.
 */
const filterDescriptorsToValues = (
  playerFilters: (RangeFilterType | SelectorFilterType)[],
  excludedPlayStyles: number[],
  excludedPlayStylesPlus: number[],
): FilterValues => {
  const player: FilterValues["player"] = {};
  const evolution: NonNullable<FilterValues["evolution"]> = {};

  const optionLabels = (filter: SelectorFilterType) =>
    filter.selectedOptions.map(
      (optionId) =>
        filter.options.find((option) => option.id === optionId)?.label ?? "",
    );

  playerFilters.forEach((filter) => {
    if (filter.type === "RANGE_FILTER") {
      const min = filter.minValue ?? undefined;
      const max = filter.maxValue ?? undefined;
      if (min === undefined && max === undefined) return;
      switch (filter.key) {
        case "rating":
          player.ratingMin = min;
          player.ratingMax = max;
          break;
        case "maxPlayStyles":
          player.maxPlayStyles = max;
          break;
        case "maxPlayStylesPlus":
          player.maxPlayStylesPlus = max;
          break;
        case "max-chain-length":
          evolution.maxChainLength = max;
          break;
        case "max-games":
          evolution.maxGames = max;
          break;
        default:
          // Face stats and in-game attributes alike; toStepFilters splits them.
          player.inGameAttributes ??= {};
          player.inGameAttributes[filter.key] = { min, max };
      }
    } else if (
      filter.type === "SELECTOR_FILTER" &&
      filter.selectedOptions.length > 0
    ) {
      switch (filter.key) {
        case "quality":
          player.quality = optionLabels(filter);
          break;
        case "skillMoves":
          player.skillMoves = filter.selectedOptions;
          break;
        case "weakFoot":
          player.weakFoot = filter.selectedOptions;
          break;
        case "position":
          player.positions = optionLabels(filter);
          break;
        case "playStyles":
          player.playStyles = filter.selectedOptions;
          break;
        case "playStylesPlus":
          player.playStylesPlus = filter.selectedOptions;
          break;
        case "accelerates":
          player.accelerates = optionLabels(filter);
          break;
        case "gender":
          player.gender = optionLabels(filter)[0];
          break;
        default:
          break;
      }
    }
  });

  if (excludedPlayStyles.length > 0) {
    player.excludedPlayStyles = excludedPlayStyles;
  }
  if (excludedPlayStylesPlus.length > 0) {
    player.excludedPlayStylesPlus = excludedPlayStylesPlus;
  }

  return {
    player,
    evolution: Object.keys(evolution).length > 0 ? evolution : undefined,
  };
};

export const evolutionBuilderFiltersAsApiParamsSelector = (
  state: IRootState,
): EvolutionBuilderApiFilters | null => {
  const { playerFilters, excludedPlayStyles, excludedPlayStylesPlus } =
    state.evolutionsBuilderReducer;
  const filters = toStepFilters(
    filterDescriptorsToValues(
      playerFilters,
      excludedPlayStyles,
      excludedPlayStylesPlus,
    ),
  );
  // Null rather than {} when nothing is active, as the callers expect.
  return Object.keys(filters).length === 0 ? null : filters;
};

/** The `/one-steps` `filters` body — shared's `StepFilters`. */
export type EvolutionBuilderApiFilters = StepFilters;

const getCurrentEvolutionPlayer = (state: EvolutionsBuilderState) => {
  return state.currentEvolutionPlayer || state.basePlayer;
};

export const getCurrentEvolutionPlayerSelector = (
  state: IRootState,
): EvolutionBuilderPlayer =>
  getCurrentEvolutionPlayer(state.evolutionsBuilderReducer);

export type PotentialPositionRoles = {
  position: string; // e.g., "ST", "CAM"
  roles: PlayerRole[];
  rewardedByEvolutions: string[]; // Evolution names
};

/** Response entries that name an evolution, as builder steps. */
const parseSteps = (entries: ResponseEvolutionStep[], isGk: boolean) =>
  entries.flatMap((entry) => {
    const step = stepFromResponse(entry, isGk);
    return step ? [step] : [];
  });

/**
 * A step as the grid shows it from `player`: the card and rating the player
 * ends up with once the step is applied.
 */
const buildStep = (
  player: EvolutionBuilderPlayer,
  step: EvolutionStep,
): EvolutionStep => {
  const next = applyEvolutionStep(player, step);
  return {
    ...step,
    metaRating: next.metaRating,
    metaRatingDelta: next.metaRating - player.metaRating,
    versionId: next.versionId,
    metalId: next.metalId,
    rating: next.rating,
    playerRoleId: next.playerRoleId,
    disabled: false,
  };
};

/**
 * Helper function that takes a current player and an evolution step,
 * and returns a new player with all the evolution step attributes added
 */
export const applyEvolutionStep = (
  currentPlayer: EvolutionBuilderPlayer,
  evolutionStep: EvolutionStep | EvolutionChainRecommendation,
): EvolutionBuilderPlayer => {
  const newPlayer = applyUpgrades(
    {
      ...currentPlayer,
      inGameAttributes: currentPlayer.inGameAttributes ?? {},
      metalId: evolutionStep.metalId || currentPlayer.metalId,
      versionId: evolutionStep.versionId || currentPlayer.versionId,
      playerRoleId: evolutionStep.playerRoleId || currentPlayer.playerRoleId,
    },
    evolutionStep.upgrades,
  );

  // Update evolution path and its pathway in step
  const appliedSoFar = evolutionPathPair(
    newPlayer.evolutionPath,
    newPlayer.pathwayPath,
  );
  if (evolutionStep.kind === "step" && evolutionStep.evolutionId) {
    newPlayer.evolutionPath = [
      ...appliedSoFar.evolutionPath,
      evolutionStep.evolutionId,
    ];
    newPlayer.pathwayPath = [
      ...appliedSoFar.pathwayPath,
      evolutionStep.pathwayPath ?? [],
    ];
  } else if (
    evolutionStep.kind === "chain" &&
    evolutionStep.evolutionIds &&
    evolutionStep.evolutionIds.length > 0
  ) {
    const added = evolutionPathPair(
      evolutionStep.evolutionIds,
      evolutionStep.pathwayPath,
    );
    newPlayer.evolutionPath = [
      ...appliedSoFar.evolutionPath,
      ...added.evolutionPath,
    ];
    newPlayer.pathwayPath = [...appliedSoFar.pathwayPath, ...added.pathwayPath];
  }
  return newPlayer;
};

export const getTotalUpgradesSelector = createSelector(
  (state: IRootState) => state.evolutionsBuilderReducer.totalUpgrades,
  groupUpgrades,
);

// TODO: add possiblity to send old/new player role id
export const getTierUpgradeSelector = ({
  state,
  metaRating,
  playerRoleId,
  playerRoleIdUpgrade,
  metaRatingUpgrade = 0,
}: {
  state: IRootState;
  metaRating: number;
  playerRoleId: number;
  playerRoleIdUpgrade: number;
  metaRatingUpgrade: number;
}): { oldTier: Tier; newTier: Tier } | null => {
  const { playerRoles } = state.playersReducer;

  const currentTier = getTier(playerRoles, metaRating, playerRoleId);

  const newTier = getTier(
    playerRoles,
    metaRating + metaRatingUpgrade,
    playerRoleIdUpgrade,
  );

  if (newTier && newTier?.tierName !== currentTier?.tierName) {
    return {
      oldTier: currentTier,
      newTier: newTier,
    };
  }
  return null;
};

export const getNoMoreStepsAvailableSelector = (state: IRootState) => {
  const {
    evolutionSteps,
    evolutionChainRecommendations,
    chainRecommendationsStatus,
    stepStatus,
    initialEvolutionSteps,
  } = state.evolutionsBuilderReducer;

  const noMoreStepsAvailable =
    evolutionSteps.every((step) => step.disabled) && stepStatus === "fulfilled";
  const noMoreChainsAvailable =
    evolutionChainRecommendations?.length === 0 &&
    chainRecommendationsStatus === "fulfilled";

  return {
    noMoreStepsAvailable: noMoreStepsAvailable && noMoreChainsAvailable,
    noEvolutionsFoundForThisPlayer:
      noMoreStepsAvailable && initialEvolutionSteps.length === 0,
  };
};

export default evolutionsBuilderSlice.reducer;

/**
 * Whether the backend withheld the chain ids.
 *
 * It sends them outright for anyone entitled to them, and omits them for a
 * player already on an evo path whose owner has to spend a credit to see the
 * steps. So the response *is* the answer — no tier or login check belongs here,
 * and the frontend must never reveal them on its own initiative: revealing is
 * the metered action, and nobody asked for it.
 */
export const chainsAwaitingRevealSelector = (state: IRootState) =>
  chainsAwaitReveal(
    state.evolutionsBuilderReducer.evolutionChainRecommendations,
  );

export const evolutionBuilderPotentialRolesSelector = (
  state: IRootState,
): PotentialPositionRoles[] => {
  const { evolutionSteps, basePlayer } = state.evolutionsBuilderReducer;
  const { evolutions } = state.evolutionsReducer;
  const { playerRoles } = state.playersReducer;

  // Get positions the player already has
  const currentPlayerPositions = basePlayer?.positions || [];

  // Map to track positions and which evolutions reward them
  const positionMap: Record<
    string,
    { evolutionNames: Set<string>; evolutionIds: Set<number> }
  > = {};

  // Extract position upgrades from evolution steps
  evolutionSteps.forEach((step) => {
    const positionUpgrades = step.upgrades.filter(
      (u): u is PositionUpgrade => u.type === "position-upgrade",
    );

    positionUpgrades.forEach((upgrade) => {
      // Skip if player already has this position
      if (currentPlayerPositions.includes(upgrade.positionId)) return;

      // Get evolution name from evolutions list
      const evolution = evolutions.find((e) => e.id === step.evolutionId);
      const evolutionName = evolution?.name || "Unknown Evolution";

      // Add to position map
      if (!positionMap[upgrade.positionId]) {
        positionMap[upgrade.positionId] = {
          evolutionNames: new Set(),
          evolutionIds: new Set(),
        };
      }

      // Only add if we haven't already added this evolution for this position
      if (!positionMap[upgrade.positionId].evolutionIds.has(step.evolutionId)) {
        positionMap[upgrade.positionId].evolutionNames.add(evolutionName);
        positionMap[upgrade.positionId].evolutionIds.add(step.evolutionId);
      }
    });
  });

  // Convert positionMap to PotentialPositionRoles array
  const potentialPositionRoles: PotentialPositionRoles[] = Object.entries(
    positionMap,
  ).map(([position, { evolutionNames }]) => {
    // Get roles for this position
    const rolesForPosition = playerRoles.filter(
      (role) => role.position === position,
    );

    return {
      position,
      roles: rolesForPosition,
      rewardedByEvolutions: Array.from(evolutionNames),
    };
  });

  return potentialPositionRoles;
};
